import MultiValueInputTable from "components/MultiValueInput";
import type { BeneficiaryAuxAccountMap } from "containers/Beneficiars/types";
import { useCallback } from "react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

const AccountingNumbersTable = ({
    codes,
    onChange,
}: {
    codes: BeneficiaryAuxAccountMap[];
    onChange: (codes: BeneficiaryAuxAccountMap[]) => void;
}) => {
    const [t] = useTranslation();

    const columns = [
        { id: "mainAccount", label: t("Beneficiars.MainAccount") },
        { id: "auxiliaryAccount", label: t("Beneficiars.AuxiliaryAccount"), isMandatory: true },
    ];

    const handleCellChange = useCallback(
        (data: {
            columnId: string;
            columnIndex: number;
            rowIndex: number;
            value: string;
            currentValue?: string | number;
        }) => {
            const { rowIndex, columnId, value } = data;

            const newCodes = codes.map((code, index) =>
                index === rowIndex
                    ? {
                          ...code,
                          [columnId]: value,
                      }
                    : code,
            );

            onChange(newCodes);
        },
        [codes, onChange],
    );

    const handleAddRow = useCallback(() => {
        onChange([...codes, { mainAccount: "", auxiliaryAccount: "" }]);
    }, [codes, onChange]);

    const handleDeleteRow = useCallback(
        (rowIndex: number) => {
            const newCodes = codes.filter((_, index) => index !== rowIndex);
            onChange(newCodes);
        },
        [codes, onChange],
    );

    return (
        <MultiValueInputTable
            columns={columns}
            rows={codes}
            onChange={handleCellChange}
            onAdd={handleAddRow}
            onDelete={handleDeleteRow}
        />
    );
};

const AccountingNumbersTableInput = ({ control, controlName }: { control: Control; controlName: string }) => {
    const [t] = useTranslation();

    return (
        <div>
            <Controller
                name={controlName}
                control={control}
                render={({ field }) => {
                    const codesAsObj: BeneficiaryAuxAccountMap[] = field.value
                        ? JSON.parse(field.value)
                        : ([] as BeneficiaryAuxAccountMap[]);

                    return (
                        <>
                            <label className="block! text-sm! font-medium! text-[#646464]! mb-1!" htmlFor={controlName}>
                                {t("Beneficiars.AccountingCode")}
                            </label>
                            <input {...field} type="text" hidden />
                            <AccountingNumbersTable
                                codes={codesAsObj}
                                onChange={(codes) => {
                                    field.onChange(JSON.stringify(codes));
                                }}
                            />
                        </>
                    );
                }}
            ></Controller>
        </div>
    );
};

export default AccountingNumbersTableInput;
